import React, { useState } from 'react';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { Box, Button, Typography, Paper, Alert } from '@mui/material';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import axios from 'axios';

const DownloadReports = ({ date }) => {
  const [open, setOpen] = useState(false);
  const [loadingType, setLoadingType] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleDownload = async (type) => {
    try {
      setLoadingType(type);
      const selectedMonth = dayjs(date).month() + 1;
      const selectedYear = dayjs(date).year();
      const token = JSON.parse(localStorage.getItem('cred'))?.token;

      const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const url = `${baseUrl}/fee-forecast/reports/${type}?month=${selectedMonth}&year=${selectedYear}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const fileName = type === 'finance' ? 'Finance_Report' : type === 'attendance' ? 'Attendance_Comparison_Report' : 'CRO_Performance_Alerts';
      link.setAttribute('download', `${fileName}_${dayjs(date).format('MMMM_YYYY')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      enqueueSnackbar('Report downloaded successfully!', { variant: 'success' });
    } catch (err) {
      let errorMessage = 'An unexpected error occurred.';
      if (err?.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (e) {
          console.error("Failed to parse error blob", e);
        }
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      const isNotFound = err?.response?.status === 404;
      enqueueSnackbar(
        isNotFound ? errorMessage : `Failed to download report. ${errorMessage}`, 
        { variant: isNotFound ? 'warning' : 'error' }
      );
    } finally {
      setLoadingType(null);
    }
  };

  return (
    <GlobalDialog
      open={open}
      setOpen={setOpen}
      title={'Download Forecast Reports'}
      closeButton={true}
      maxWidth={'sm'}
      fullWidth
      popupButton={
        <Button
          startIcon={<Icon icon="mdi:download-circle" width="24" height="24" />}
          size="small"
          variant="contained"
          color="primary"
          onClick={() => setOpen(true)}
        >
          Download Reports
        </Button>
      }
      content={
        <Box sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ mb: 2.5 }}>
            Download the <strong>Monthly Finance Report</strong>, <strong>Attendance Comparison Report</strong>, or <strong>CRO Performance Alerts</strong> for the selected month: <strong>{dayjs(date).format('MMMM YYYY')}</strong>.
          </Alert>

          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="700">Finance Report</Typography>
              <Typography variant="body2" color="text.secondary">Target vs Actual Collection</Typography>
            </Box>
            <Button 
                variant="contained" 
                onClick={() => handleDownload('finance')}
                disabled={loadingType === 'finance'}
                startIcon={loadingType === 'finance' ? <Icon icon="line-md:loading-loop" /> : <Icon icon="mdi:download" />}
            >
              {loadingType === 'finance' ? 'Downloading...' : 'Download'}
            </Button>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="700">Attendance Report</Typography>
              <Typography variant="body2" color="text.secondary">Expected vs Conducted Sessions</Typography>
            </Box>
            <Button 
                variant="contained" 
                color="secondary"
                onClick={() => handleDownload('attendance')}
                disabled={loadingType === 'attendance'}
                startIcon={loadingType === 'attendance' ? <Icon icon="line-md:loading-loop" /> : <Icon icon="mdi:download" />}
            >
              {loadingType === 'attendance' ? 'Downloading...' : 'Download'}
            </Button>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="700">CRO Performance Alerts</Typography>
              <Typography variant="body2" color="text.secondary">Underperforming CROs (&lt;85% for 3 months)</Typography>
            </Box>
            <Button 
                variant="contained" 
                color="error"
                onClick={() => handleDownload('cro-performance')}
                disabled={loadingType === 'cro-performance'}
                startIcon={loadingType === 'cro-performance' ? <Icon icon="line-md:loading-loop" /> : <Icon icon="mdi:download" />}
            >
              {loadingType === 'cro-performance' ? 'Downloading...' : 'Download'}
            </Button>
          </Paper>
        </Box>
      }
    />
  );
};

export default DownloadReports;
